// ─── Knowledge Capture Pipeline — Unified Knowledge Item ─────────────────────
// Every capture source (PDF, website, YouTube, podcast, voice note, photo,
// note, link) produces exactly one KnowledgeItem. This is the canonical
// object stored in IndexedDB and rendered in the Backpack, Search, and
// Collections surfaces.

/** Which capture button/flow produced the item. */
export type KnowledgeSourceType =
  | 'file'
  | 'website'
  | 'youtube'
  | 'podcast'
  | 'voice'
  | 'photo'
  | 'note';

/** What the content actually is (drives icons, badges, filters). */
export type KnowledgeContentType =
  | 'pdf'
  | 'document'      // docx, pptx
  | 'spreadsheet'   // xlsx, csv
  | 'ebook'         // epub
  | 'text'          // txt, md
  | 'website'
  | 'youtube'
  | 'podcast'
  | 'audio'         // uploaded/recorded audio files
  | 'voice'         // in-app voice note
  | 'photo'
  | 'note';

export type KnowledgeProcessingStatus = 'pending' | 'processing' | 'complete' | 'failed';

export type IntelligenceStatus = 'pending' | 'processing' | 'complete' | 'skipped';

/** The five live pipeline stages shown in the capture tray. */
export type PipelineStage = 'capture' | 'extract' | 'understand' | 'organize' | 'store';

export const PIPELINE_STAGES: { id: PipelineStage; label: string }[] = [
  { id: 'capture',    label: 'Capture'    },
  { id: 'extract',    label: 'Extract'    },
  { id: 'understand', label: 'Understand' },
  { id: 'organize',   label: 'Organize'   },
  { id: 'store',      label: 'Store'      },
];

export interface Flashcard {
  question: string;
  answer: string;
}

export interface KnowledgeItem {
  id: string;
  title: string;

  sourceType: KnowledgeSourceType;
  contentType: KnowledgeContentType;

  /** URL for web sources, filename for files, null for notes/voice. */
  originalSource: string | null;

  /** Site / channel / show / author — whatever "who published this" means. */
  publisher: string | null;

  /** Full extracted text (article body, transcript, OCR text, note markdown). */
  rawContent: string;

  /** Sanitizable readable HTML (websites) — rendered in the detail view. */
  contentHtml: string | null;

  // ── AI outputs (Understand stage) ──────────────────────────────────────────
  summary: string | null;
  keyPoints: string[];
  insights: string[];
  quotes: string[];
  actionItems: string[];
  /** Action items the user has checked off (indexes into actionItems). */
  completedActions: number[];
  flashcards: Flashcard[];
  suggestedMissions: string[];
  suggestedOpportunities: string[];

  // ── Organization ───────────────────────────────────────────────────────────
  tags: string[];
  categories: string[];
  /** User-collection ids this item belongs to. */
  collections: string[];
  /**
   * Concept terms generated by the AI for semantic-style retrieval
   * (synonyms, related terms, questions the content answers).
   */
  searchIndex: string[];
  /**
   * Reserved for real vector embeddings once an embeddings-capable backend
   * is connected. The AI Integrations proxy does not expose an embeddings
   * API today, so hybrid search runs on searchIndex terms instead.
   */
  embeddings: number[] | null;
  language: string;

  // ── Media / file metadata ──────────────────────────────────────────────────
  /** Data URL (generated thumbnails) or https URL (remote thumbnails). */
  thumbnailUrl: string | null;
  fileName: string | null;
  fileSize: number | null;
  fileMimeType: string | null;
  /** Lowercase extension, e.g. "pdf", "docx". */
  fileType: string | null;
  pageCount: number | null;
  durationSec: number | null;
  wordCount: number;
  readingTimeMin: number;

  // ── Pipeline state ─────────────────────────────────────────────────────────
  processingStatus: KnowledgeProcessingStatus;
  processingError: string | null;

  /** Where the original binary lives: 'indexeddb' blob store, Cloudflare R2, or 'none'. */
  storageLocation: 'indexeddb' | 'r2' | 'none';
  /** Cloudflare R2 file_objects.id when storageLocation is 'r2'. Null otherwise. */
  r2FileId?: number | null;

  /** SHA-256 of the source (URL or file bytes) for duplicate detection. */
  sourceHash: string | null;

  favorite: boolean;
  dateAdded: string;
  processedAt: string | null;

  // ── Roamer Intelligence Engine™ outputs ────────────────────────────────────
  recognizedCompanies: string[];
  recognizedPeople: string[];
  recognizedPlaces: string[];
  detectedSkills: string[];
  detectedCareerTypes: string[];
  topicsAi: string[];
  keywords: string[];
  relatedContent: string[];
  intelligenceStatus: IntelligenceStatus;
  intelligenceStage: string | null;
  intelligenceProcessedAt: string | null;
  /** Sprint 115 — pin this item to the top of all intelligence features. */
  rememberForMe: boolean;
}

export interface UserCollection {
  id: string;
  name: string;
  color: string;
  /** Lucide icon name. */
  iconName: string;
  favorite: boolean;
  /** True when created automatically from an AI suggestion. */
  aiSuggested: boolean;
  createdAt: string;
}

/** A live pipeline job shown in the Processing Tray. */
export interface CaptureJob {
  id: string;
  label: string;
  sourceType: KnowledgeSourceType;
  stage: PipelineStage | 'done' | 'error' | 'duplicate';
  /** 0-100 across the whole pipeline. */
  pct: number;
  /** Human-readable current step, e.g. "Reading page 4 of 12". */
  detail: string;
  error: string | null;
  itemId: string | null;
  startedAt: string;
  finishedAt: string | null;
}

/** A live Roamer Intelligence Engine™ job shown in the tray. */
export interface IntelligenceJob {
  id: string;
  label: string;
  itemId: string;
  stage: string;
  stageIndex: number;
  totalStages: number;
  pct: number;
  detail: string;
  status: 'running' | 'done' | 'error';
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
}

/** The 21 stages of the Roamer Intelligence Engine™ pipeline. */
export const INTELLIGENCE_STAGES: { id: string; label: string }[] = [
  { id: 'rie-capture',     label: 'Capture'                  },
  { id: 'rie-detect',      label: 'Identify Content Type'    },
  { id: 'rie-meta',        label: 'Extract Metadata'         },
  { id: 'rie-text',        label: 'Extract Text'             },
  { id: 'rie-ocr',         label: 'OCR'                      },
  { id: 'rie-transcript',  label: 'Transcript'               },
  { id: 'rie-summary',     label: 'Generate Roamer Summary'  },
  { id: 'rie-topics',      label: 'Extract Topics'           },
  { id: 'rie-keywords',    label: 'Extract Keywords'         },
  { id: 'rie-companies',   label: 'Recognize Companies'      },
  { id: 'rie-people',      label: 'Recognize People'         },
  { id: 'rie-places',      label: 'Recognize Places'         },
  { id: 'rie-skills',      label: 'Recognize Skills'         },
  { id: 'rie-careers',     label: 'Recognize Career Types'   },
  { id: 'rie-collections', label: 'Suggest Collections'      },
  { id: 'rie-related',     label: 'Generate Related Content' },
  { id: 'rie-search',      label: 'Create Search Index'      },
  { id: 'rie-graph',       label: 'Update Knowledge Graph'   },
  { id: 'rie-store-orig',  label: 'Store Original'           },
  { id: 'rie-store-intel', label: 'Store Intelligence'       },
  { id: 'rie-complete',    label: 'Complete'                 },
];

export const KNOWLEDGE_EVENTS = {
  /** Items or collections changed in the store. */
  updated: 'nomad:knowledge-updated',
  /** Jobs snapshot changed (progress, completion, errors). */
  jobs: 'nomad:knowledge-jobs',
} as const;

export const INTELLIGENCE_EVENTS = {
  /** Intelligence jobs snapshot changed. */
  jobs: 'nomad:intelligence-jobs',
} as const;

/** Colors cycled through when auto-creating AI-suggested collections. */
export const COLLECTION_COLORS = [
  '#a855f7', '#3b82f6', '#22c55e', '#f59e0b',
  '#06b6d4', '#ef4444', '#8b5cf6', '#14b8a6',
];

export function estimateReadingTimeMin(wordCount: number): number {
  return Math.max(1, Math.ceil(wordCount / 220));
}

export function countWords(text: string): number {
  const t = text.trim();
  return t.length === 0 ? 0 : t.split(/\s+/).length;
}
